import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, Loading, EmptyState } from "@/components/common/ui";
import { KpiTile } from "@/components/KpiTile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  PiggyBank, Save, RotateCcw, ChevronRight, Lock, Receipt, Coins,
  TrendingUp, TrendingDown, PauseCircle, Info, Layers, PieChart, CalendarDays, Minus,
} from "lucide-react";

const FY = "FY2026-27";
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const cr = (v) => `\u20B9${(Number(v) || 0).toFixed(2)} Cr`;
const pctStr = (v) => `${(Math.round((Number(v) || 0) * 10) / 10)}%`;

function toDraft(rows, months, vertKeys, costKeys) {
  const d = {};
  for (const r of rows) {
    d[r.project] = {
      sale_value_cr: String(r.sale_value_cr ?? 0),
      budget_pct: String(r.budget_pct ?? 0),
      vertical_pct: Object.fromEntries(vertKeys.map((k) => [k, String(r.vertical_pct?.[k] ?? 0)])),
      cost_pct: Object.fromEntries(costKeys.map((k) => [k, String(r.cost_pct?.[k] ?? 0)])),
      months: Object.fromEntries(months.map((m) => [m, { pct: String(r.months?.[m]?.pct ?? 0), hold: !!r.months?.[m]?.hold }])),
    };
  }
  return d;
}

function NumInput({ value, onChange, editable, disabled, suffix = "%", testId, w = "w-[70px]" }) {
  if (!editable) {
    return <span className="num font-mono text-xs tabular-nums text-muted-foreground" data-testid={testId}>{value}{suffix}</span>;
  }
  return (
    <div className={cn("relative ml-auto", w)}>
      <Input
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        inputMode="decimal"
        className={cn("h-8 pr-5 text-right text-xs font-mono tabular-nums", disabled && "opacity-40")}
        data-testid={testId}
      />
      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground">{suffix}</span>
    </div>
  );
}

function VarChip({ planned, actual, testId }) {
  const v = (Number(planned) || 0) - (Number(actual) || 0);
  const abs = Math.abs(v);
  if (abs < 0.005) {
    return <Badge variant="outline" className="gap-1 border-border text-[10px] text-muted-foreground tabular-nums" data-testid={testId}><Minus className="h-3 w-3" />On plan</Badge>;
  }
  const under = v > 0;
  return (
    <Badge variant="outline" className={cn("gap-1 text-[10px] tabular-nums",
      under ? "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]" : "border-[hsl(var(--danger)/0.45)] text-[hsl(var(--danger))]")}
      data-testid={testId}>
      {under ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
      {under ? "Under" : "Over"} {cr(abs)}
    </Badge>
  );
}

export default function BudgetAllocation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [initial, setInitial] = useState({});
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("plan");

  const load = () => {
    setLoading(true);
    return api.budgetPlanner(FY).then((d) => {
      setData(d);
      const vk = d.verticals.map((v) => v.key);
      const ck = d.cost_categories.map((c) => c.key);
      const dr = toDraft(d.rows, d.months, vk, ck);
      setDraft(dr); setInitial(JSON.parse(JSON.stringify(dr)));
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("Could not load the budget plan"); });
  };
  useEffect(() => { load(); }, []);

  const editable = !!data?.editable;
  const months = data?.months || [];
  const verticals = data?.verticals || [];
  const costCats = data?.cost_categories || [];

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const derived = useMemo(() => {
    const rows = (data?.rows || []).map((base) => {
      const d = draft[base.project];
      if (!d) return base;
      const sale = num(d.sale_value_cr);
      const bpct = num(d.budget_pct);
      const estimate = (sale * bpct) / 100;
      let planned = 0;
      const monthsOut = {};
      for (const m of months) {
        const me = d.months[m] || { pct: 0, hold: false };
        const pcr = me.hold ? 0 : (estimate * num(me.pct)) / 100;
        monthsOut[m] = { pct: me.pct, hold: me.hold, planned_cr: pcr };
        planned += pcr;
      }
      const vertPlanned = Object.fromEntries(verticals.map((v) => [v.key, (estimate * num(d.vertical_pct[v.key])) / 100]));
      const costPlanned = Object.fromEntries(costCats.map((c) => [c.key, (estimate * num(d.cost_pct[c.key])) / 100]));
      const vertSum = verticals.reduce((s, v) => s + num(d.vertical_pct[v.key]), 0);
      const costSum = costCats.reduce((s, c) => s + num(d.cost_pct[c.key]), 0);
      const monthSum = months.reduce((s, m) => s + num((d.months[m] || {}).pct), 0);
      return {
        ...base, sale_value_cr: sale, budget_pct: bpct, estimate_cr: estimate,
        planned_cr: planned, monthsOut, vertPlanned, costPlanned,
        vertSum, costSum, monthSum,
        variance_cr: planned - base.actual_cr,
      };
    });
    const totals = rows.reduce((a, r) => ({
      sale_value_cr: a.sale_value_cr + r.sale_value_cr,
      estimate_cr: a.estimate_cr + r.estimate_cr,
      planned_cr: a.planned_cr + r.planned_cr,
      actual_cr: a.actual_cr + (r.actual_cr || 0),
    }), { sale_value_cr: 0, estimate_cr: 0, planned_cr: 0, actual_cr: 0 });
    totals.variance_cr = totals.planned_cr - totals.actual_cr;
    return { rows, totals };
  }, [draft, data, months, verticals, costCats]);

  const setField = (project, field, value) => setDraft((p) => ({ ...p, [project]: { ...p[project], [field]: value } }));
  const setVert = (project, key, value) => setDraft((p) => ({ ...p, [project]: { ...p[project], vertical_pct: { ...p[project].vertical_pct, [key]: value } } }));
  const setCost = (project, key, value) => setDraft((p) => ({ ...p, [project]: { ...p[project], cost_pct: { ...p[project].cost_pct, [key]: value } } }));
  const setMonth = (project, m, patch) => setDraft((p) => ({ ...p, [project]: { ...p[project], months: { ...p[project].months, [m]: { ...p[project].months[m], ...patch } } } }));

  const save = async () => {
    setSaving(true);
    const rows = Object.keys(draft).map((project) => {
      const d = draft[project];
      return {
        project,
        sale_value_cr: num(d.sale_value_cr),
        budget_pct: num(d.budget_pct),
        vertical_pct: Object.fromEntries(Object.entries(d.vertical_pct).map(([k, v]) => [k, num(v)])),
        cost_pct: Object.fromEntries(Object.entries(d.cost_pct).map(([k, v]) => [k, num(v)])),
        months: Object.fromEntries(Object.entries(d.months).map(([m, e]) => [m, { pct: num(e.pct), hold: !!e.hold }])),
      };
    });
    try {
      const res = await api.saveBudgetPlanner(FY, rows);
      toast.success(`Budget plan saved`, { description: `${res.updated} project plan(s) updated. Planned vs actual now reflects your changes.` });
      await load();
    } catch (e) {
      toast.error("Could not save budget plan", { description: e?.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  if (loading && !data) return <div className="space-y-5"><Loading rows={6} /></div>;
  if (!data) return <EmptyState title="No plan" message="Could not load the budget plan." />;

  const t = derived.totals;
  const drows = derived.rows;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5 pb-24" data-testid="budget-allocation">
        <PageHeader title="Budget Allocation"
          description="Plan each project's annual marketing budget from planned Sale Value & Budget %, split it across 12 months and verticals, then track Planned vs Actual live. Put a project-month on hold when a campaign pauses.">
          {!editable && (
            <span className="inline-flex items-center gap-1.5 rounded-md border bg-[hsl(var(--surface-2))] px-2.5 py-1.5 text-xs text-muted-foreground" data-testid="planner-readonly">
              <Lock className="h-3.5 w-3.5" /> View only — admin can edit
            </span>
          )}
        </PageHeader>

        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiTile testId="planner-kpi-sale" title="Planned Sale Value" accent="hsl(var(--chart-cat-4))" icon={TrendingUp} value={<span className="font-mono">{cr(t.sale_value_cr)}</span>} sub="Revenue we plan to earn" />
          <KpiTile testId="planner-kpi-planned" title="Planned Budget" accent="hsl(var(--primary))" icon={PiggyBank} value={<span className="font-mono">{cr(t.planned_cr)}</span>} sub={`Estimate ${cr(t.estimate_cr)} across 12 months`} />
          <KpiTile testId="planner-kpi-actual" title="Actual Spend" accent="hsl(var(--warning))" icon={Receipt} value={<span className="font-mono">{cr(t.actual_cr)}</span>} sub="Live from the spend ledger" />
          <KpiTile testId="planner-kpi-variance" title="Variance" accent={t.variance_cr < 0 ? "hsl(var(--danger))" : "hsl(var(--success))"} icon={Coins}
            value={<span className="font-mono">{cr(Math.abs(t.variance_cr))}</span>} sub={t.variance_cr < 0 ? "Spent beyond plan" : "Under the plan"} />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="planner-tabs">
            <TabsTrigger value="plan" data-testid="planner-tab-plan"><PiggyBank className="mr-1.5 h-3.5 w-3.5" />Plan &amp; Actuals</TabsTrigger>
            <TabsTrigger value="vertical" data-testid="planner-tab-vertical"><Layers className="mr-1.5 h-3.5 w-3.5" />Vertical Split</TabsTrigger>
            <TabsTrigger value="cost" data-testid="planner-tab-cost"><PieChart className="mr-1.5 h-3.5 w-3.5" />Cost Split</TabsTrigger>
          </TabsList>

          {/* PLAN & ACTUALS */}
          <TabsContent value="plan" className="mt-4">
            <SectionCard title="Per-Project Plan vs Actual"
              description="Enter Sale Value (₹ Cr) and Budget %. Estimate Spend = Sale Value × Budget %. Expand a project to plan each month and toggle Hold for paused campaigns."
              testId="planner-plan-card">
              <div className="table-scroll">
                <table className="w-full border-collapse text-sm" data-testid="planner-plan-table">
                  <thead>
                    <tr>
                      {["Project", "Sale Value", "Budget %", "Estimate", "Planned", "Actual", "Variance"].map((h, i) => (
                        <th key={h} className={cn("th-sticky px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", i === 0 ? "th-sticky-left text-left" : "text-right")}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drows.map((r, i) => {
                      const isOpen = !!open[r.project];
                      return (
                        <React.Fragment key={r.project}>
                          <tr className={cn("border-b last:border-0 hover:bg-[hsl(var(--accent)/0.35)]", i % 2 && "row-zebra")} data-testid={`planner-row-${r.project}`}>
                            <td className="td-sticky-left px-3 py-2">
                              <button type="button" onClick={() => setOpen((o) => ({ ...o, [r.project]: !o[r.project] }))}
                                className="inline-flex items-center gap-1.5 text-left font-medium hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded" data-testid={`planner-expand-${r.project}`}>
                                <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform duration-150", isOpen && "rotate-90")} />
                                {r.project}
                              </button>
                            </td>
                            <td className="px-3 py-2"><NumInput editable={editable} suffix="Cr" w="w-[92px]" value={draft[r.project]?.sale_value_cr} onChange={(v) => setField(r.project, "sale_value_cr", v)} testId={`planner-sale-${r.project}`} /></td>
                            <td className="px-3 py-2"><NumInput editable={editable} value={draft[r.project]?.budget_pct} onChange={(v) => setField(r.project, "budget_pct", v)} testId={`planner-budget-${r.project}`} /></td>
                            <td className="px-3 py-2 num font-mono text-xs tabular-nums text-muted-foreground" data-testid={`planner-estimate-${r.project}`}>{cr(r.estimate_cr)}</td>
                            <td className="px-3 py-2 num font-mono text-xs font-semibold tabular-nums" data-testid={`planner-planned-${r.project}`}>{cr(r.planned_cr)}</td>
                            <td className="px-3 py-2 num font-mono text-xs tabular-nums" data-testid={`planner-actual-${r.project}`}>{cr(r.actual_cr)}</td>
                            <td className="px-3 py-2 text-right"><VarChip planned={r.planned_cr} actual={r.actual_cr} testId={`planner-var-${r.project}`} /></td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-[hsl(var(--surface-2))]" data-testid={`planner-months-${r.project}`}>
                              <td colSpan={7} className="px-3 py-3">
                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                                  {months.map((m) => {
                                    const me = r.monthsOut[m];
                                    const am = r.actual_by_month?.[m] || 0;
                                    return (
                                      <div key={m} className={cn("rounded-lg border bg-card p-2.5", me.hold && "opacity-70")} data-testid={`planner-month-${r.project}-${m}`}>
                                        <div className="flex items-center justify-between">
                                          <span className="inline-flex items-center gap-1 text-xs font-medium"><CalendarDays className="h-3 w-3 text-muted-foreground" />{m}</span>
                                          <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                                            <Switch checked={me.hold} disabled={!editable} onCheckedChange={(v) => setMonth(r.project, m, { hold: v })} data-testid={`planner-hold-${r.project}-${m}`} />
                                            Hold
                                          </label>
                                        </div>
                                        <div className="mt-2 flex items-center justify-between gap-2">
                                          <NumInput editable={editable} disabled={me.hold} w="w-[62px]" value={(draft[r.project]?.months?.[m] || {}).pct} onChange={(v) => setMonth(r.project, m, { pct: v })} testId={`planner-monthpct-${r.project}-${m}`} />
                                          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{me.hold ? <span className="inline-flex items-center gap-1 text-[hsl(var(--hold))]"><PauseCircle className="h-3 w-3" />Paused</span> : cr(me.planned_cr)}</span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                                          <span>Actual</span><span className="font-mono tabular-nums">{cr(am)}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <p className={cn("mt-2 text-[11px]", Math.abs(r.monthSum - 100) < 0.5 ? "text-muted-foreground" : "text-[hsl(var(--warning))]")} data-testid={`planner-monthsum-${r.project}`}>
                                  Monthly split totals {pctStr(r.monthSum)} of the annual estimate {Math.abs(r.monthSum - 100) < 0.5 ? "\u2713" : "\u2014 aim for 100%"}
                                </p>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <tr className="row-total border-t-2">
                      <td className="td-sticky-left px-3 py-2.5 text-sm font-semibold">Total</td>
                      <td className="px-3 py-2.5 num font-mono text-xs font-semibold tabular-nums">{cr(t.sale_value_cr)}</td>
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5 num font-mono text-xs font-semibold tabular-nums">{cr(t.estimate_cr)}</td>
                      <td className="px-3 py-2.5 num font-mono text-xs font-semibold tabular-nums">{cr(t.planned_cr)}</td>
                      <td className="px-3 py-2.5 num font-mono text-xs font-semibold tabular-nums">{cr(t.actual_cr)}</td>
                      <td className="px-3 py-2.5 text-right"><VarChip planned={t.planned_cr} actual={t.actual_cr} testId="planner-var-total" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </TabsContent>

          {/* VERTICAL SPLIT */}
          <TabsContent value="vertical" className="mt-4">
            <SectionCard title="Vertical Split — Planned vs Actual"
              description="Set each project's budget split across ATL/BTL, Digital, CX & Brand (% of the estimate spend). CP is excluded. Actuals are mapped from your spend verticals."
              testId="planner-vertical-card">
              <div className="table-scroll">
                <table className="w-full border-collapse text-sm" data-testid="planner-vertical-table">
                  <thead>
                    <tr>
                      <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Project</th>
                      {verticals.map((v) => (
                        <th key={v.key} className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          <span className="inline-flex items-center gap-1">{v.label}
                            <Tooltip><TooltipTrigger asChild><Badge variant="outline" className={cn("text-[9px]", v.tag === "Visibility" ? "border-[hsl(var(--info)/0.4)] text-[hsl(var(--info))]" : "border-[hsl(var(--chart-cat-6)/0.5)] text-[hsl(var(--chart-cat-6))]")}>{v.tag[0]}</Badge></TooltipTrigger><TooltipContent>{v.tag}</TooltipContent></Tooltip>
                          </span>
                        </th>
                      ))}
                      <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drows.map((r, i) => (
                      <tr key={r.project} className={cn("border-b last:border-0", i % 2 && "row-zebra")} data-testid={`planner-vrow-${r.project}`}>
                        <td className="td-sticky-left px-3 py-2 text-sm font-medium">{r.project}</td>
                        {verticals.map((v) => {
                          const av = r.actual_by_vertical?.[v.key] || 0;
                          return (
                            <td key={v.key} className="px-3 py-2">
                              <div className="flex flex-col items-end gap-0.5">
                                <NumInput editable={editable} value={(draft[r.project]?.vertical_pct || {})[v.key]} onChange={(val) => setVert(r.project, v.key, val)} testId={`planner-vpct-${r.project}-${v.key}`} />
                                <span className="font-mono text-[10px] tabular-nums text-muted-foreground">plan {cr(r.vertPlanned[v.key])}</span>
                                <span className="font-mono text-[10px] tabular-nums text-[hsl(var(--warning))]">act {cr(av)}</span>
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right">
                          <Badge variant="outline" className={cn("text-[10px] tabular-nums", Math.abs(r.vertSum - 100) < 0.5 ? "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]" : "border-[hsl(var(--warning)/0.5)] text-[hsl(var(--warning))]")} data-testid={`planner-vsum-${r.project}`}>{pctStr(r.vertSum)}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="flex items-center gap-1.5 px-3 py-2 text-[11px] text-muted-foreground"><Info className="h-3.5 w-3.5" />Mapping — ATL/BTL ← ATL+BTL · Digital ← Digital+Digital Support · CX ← CX+CXX · Brand ← Brand.</p>
            </SectionCard>
          </TabsContent>

          {/* COST SPLIT */}
          <TabsContent value="cost" className="mt-4">
            <SectionCard title="Cost-Category Split — Planned targets"
              description="Planned-only cost mix per project (% of the estimate spend). CP Mandate & SIB are intentionally excluded."
              testId="planner-cost-card">
              <div className="table-scroll">
                <table className="w-full border-collapse text-sm" data-testid="planner-cost-table">
                  <thead>
                    <tr>
                      <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Project</th>
                      {costCats.map((c) => (
                        <th key={c.key} className="th-sticky px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{c.label}</th>
                      ))}
                      <th className="th-sticky px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drows.map((r, i) => (
                      <tr key={r.project} className={cn("border-b last:border-0", i % 2 && "row-zebra")} data-testid={`planner-crow-${r.project}`}>
                        <td className="td-sticky-left px-3 py-2 text-sm font-medium">{r.project}</td>
                        {costCats.map((c) => (
                          <td key={c.key} className="px-2 py-2">
                            <div className="flex flex-col items-end gap-0.5">
                              <NumInput editable={editable} w="w-[58px]" value={(draft[r.project]?.cost_pct || {})[c.key]} onChange={(val) => setCost(r.project, c.key, val)} testId={`planner-cpct-${r.project}-${c.key}`} />
                              <span className="font-mono text-[10px] tabular-nums text-muted-foreground">{cr(r.costPlanned[c.key])}</span>
                            </div>
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right">
                          <Badge variant="outline" className={cn("text-[10px] tabular-nums", Math.abs(r.costSum - 100) < 0.5 ? "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]" : "border-[hsl(var(--warning)/0.5)] text-[hsl(var(--warning))]")} data-testid={`planner-csum-${r.project}`}>{pctStr(r.costSum)}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </TabsContent>
        </Tabs>

        {editable && dirty && (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-[hsl(var(--card)/0.92)] backdrop-blur-md" data-testid="planner-save-bar">
            <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-3 px-6 py-3">
              <span className="text-sm text-muted-foreground">You have unsaved budget changes.</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setDraft(JSON.parse(JSON.stringify(initial)))} disabled={saving} data-testid="planner-discard"><RotateCcw className="mr-1.5 h-4 w-4" />Discard</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="planner-save"><Save className="mr-1.5 h-4 w-4" />{saving ? "Saving\u2026" : "Save Budget Plan"}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
